import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { type ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  type LinkProjectRunEnvironmentRequestBody,
  type ListProjectRunEnvironmentLinksResponseBody,
} from "common";

// GOV-2: hooks over the run-environment project-sharing routes. Linking only ADDS visibility,
// so the picker (server side) needs no change; these hooks drive the run-environment form control.
const linkedProjectsQueryKey = (profileClusterId: number) => [
  "/api/v1/run-environment/projects/list",
  profileClusterId,
];

export const useLinkedProjects = (
  profileClusterId: number | undefined,
  enabled: boolean = true
): UseQueryResult<number[], Error> => {
  return useQuery<number[], Error>({
    queryKey: linkedProjectsQueryKey(profileClusterId ?? 0),
    queryFn: async () => {
      const res = await axios.post("/api/v1/run-environment/projects/list", {
        profileClusterId,
      });
      return (res.data as ListProjectRunEnvironmentLinksResponseBody)
        .projectIds;
    },
    enabled: enabled && !!profileClusterId,
    retry: false,
  });
};

export const useLinkProjectToEnvironment = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    void,
    ErrorNotification,
    LinkProjectRunEnvironmentRequestBody
  >({
    mutationFn: async (body: LinkProjectRunEnvironmentRequestBody) => {
      await axios.post("/api/v1/run-environment/projects/link", body);
    },
    onSuccess: (_result, variables): void => {
      void queryClient.invalidateQueries({
        queryKey: linkedProjectsQueryKey(variables.profileClusterId),
      });
    },
    onError: (error: ErrorNotification): void => {
      setNotification({
        id: "run-env-project-link-error",
        message:
          error?.message ??
          "Could not share the run environment with the project",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUnlinkProjectFromEnvironment = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    void,
    ErrorNotification,
    LinkProjectRunEnvironmentRequestBody
  >({
    mutationFn: async (body: LinkProjectRunEnvironmentRequestBody) => {
      await axios.post("/api/v1/run-environment/projects/unlink", body);
    },
    onSuccess: (_result, variables): void => {
      void queryClient.invalidateQueries({
        queryKey: linkedProjectsQueryKey(variables.profileClusterId),
      });
    },
    onError: (error: ErrorNotification): void => {
      setNotification({
        id: "run-env-project-unlink-error",
        message:
          error?.message ??
          "Could not remove the project from the run environment",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
